import { useMemo, useRef, useState } from 'react';
import { checklistOf, subtasksOf, type ChecklistItem, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { useSubtaskSort } from '../lib/subtaskSort';
import { usePlan } from '../lib/plan';
import { userAvatarUrl } from '../lib/api';
import { Icon } from '../lib/icons';
import { avatarGradient, initials, shortDate } from '../lib/format';
import { Avatar, Popover } from './ui';

// Gliederung einer Aufgabe in der Detailspalte: Unteraufgaben (vollwertige
// Aufgaben mit eigenem Chat, eine Ebene) und die Checkliste (leichte Punkte
// ohne Chat). Ein Checklistenpunkt lässt sich zur Unteraufgabe machen.

function Progress({ done, total }: { done: number; total: number }) {
  if (!total) return null;
  return (
    <span className="ml-auto flex items-center gap-2 text-[12px] font-medium text-[var(--muted)]">
      {done}/{total}
      <span className="progbar"><span style={{ width: `${Math.round((done / total) * 100)}%` }} /></span>
    </span>
  );
}

export function SubtaskSection({ task, onOpenTask }: { task: Task; onOpenTask: (id: string) => void }) {
  const { client, me, version } = useSync();
  const plan = usePlan();
  const sortable = useSubtaskSort();
  const [title, setTitle] = useState('');
  const subs = useMemo(() => subtasksOf(client, task.id), [client, version, task.id]);
  const parent = task.parentId ? client.data.task.get(task.parentId) : null;
  // Zuordnen: offene Hauptaufgaben im selben Projekt (ohne eigene Hauptaufgabe)
  const candidates = useMemo(
    () =>
      [...client.data.task.values()]
        .filter((t) => t.projectId === task.projectId && t.id !== task.id && !t.parentId && t.status !== 'erledigt')
        .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt))
        .slice(0, 30),
    [client, version, task.id, task.projectId],
  );
  const setParent = (parentId: string | null) => void client.saveTask({ id: task.id, projectId: task.projectId, title: task.title, parentId });

  // Unteraufgabe: Verweis auf die Hauptaufgabe, keine eigenen Unteraufgaben
  if (parent) {
    return (
      <section>
        <h4 className="mb-2 text-[13px] font-semibold text-[var(--muted)]">Hauptaufgabe</h4>
        <div className="flex items-center gap-2">
          <button className="row !w-auto min-w-0 flex-1 !py-1.5" onClick={() => onOpenTask(parent.id)}>
            <Icon name="liste" size={17} className="text-[var(--brand-a)]" />
            <span className="min-w-0 flex-1 truncate text-[14px]">{parent.title}</span>
            <Icon name="chev" size={14} className="text-[var(--dim)]" />
          </button>
          <button className="chip" onClick={() => setParent(null)} data-tip="Wird zu einer eigenständigen Aufgabe">Lösen</button>
        </div>
      </section>
    );
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t || !plan.more('openTasks')) return;
    setTitle('');
    await client.saveTask({ projectId: task.projectId, title: t, parentId: task.id });
  }

  const done = subs.filter((s) => s.status === 'erledigt').length;
  return (
    <section>
      <h4 className="mb-2 flex items-center text-[13px] font-semibold text-[var(--muted)]">
        Unteraufgaben
        <Progress done={done} total={subs.length} />
      </h4>
      {subs.length > 0 && (
        <ul className="m-0 mb-2 flex list-none flex-col gap-0.5 p-0">
          {subs.map((s) => {
            const who = me.members.find((m) => m.userId === s.assigneeIds[0]);
            const closed = s.status === 'erledigt';
            return (
              <li key={s.id} className="subitem" {...sortable(s)}>
                <span className="subdrag" aria-hidden="true" title="Zum Sortieren ziehen">⠿</span>
                <button
                  className={`checkbox ${closed ? 'on' : ''}`}
                  role="checkbox"
                  aria-checked={closed}
                  aria-label={closed ? `${s.title} wieder öffnen` : `${s.title} erledigen`}
                  onClick={() => (closed ? plan.more('openTasks') : true) && void client.saveTask({ id: s.id, projectId: s.projectId, title: s.title, status: closed ? 'offen' : 'erledigt' })}
                >
                  {closed && <Icon name="check" size={13} />}
                </button>
                <button className="min-w-0 flex-1 truncate text-left text-[14px]" onClick={() => onOpenTask(s.id)} data-tip="Öffnen">
                  <span className={closed ? 'text-[var(--muted)] line-through' : ''}>{s.title}</span>
                </button>
                {s.dueAt && !closed && <span className="flex-none text-[12px] text-[var(--muted)]">{shortDate(s.dueAt)}</span>}
                {who && <Avatar name={initials(who.name)} gradient={avatarGradient(who.userId)} src={userAvatarUrl(who.userId, who.avatarAt)} size={22} />}
              </li>
            );
          })}
        </ul>
      )}
      <form className="flex gap-2" onSubmit={(e) => void add(e)}>
        <input className="input h-9 min-w-0 flex-1 text-[14px]" placeholder="Unteraufgabe hinzufügen" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} aria-label="Neue Unteraufgabe" />
        {title.trim() && <button className="btn btn-primary h-9">Anlegen</button>}
      </form>
      {subs.length === 0 && candidates.length > 0 && (
        <Popover
          width={300}
          trigger={({ toggle, open }) => (
            <button className="mt-2 text-[12.5px] text-[var(--muted)] underline decoration-dotted" aria-expanded={open} onClick={toggle}>
              Diese Aufgabe einer anderen unterordnen
            </button>
          )}
        >
          {(close) => (
            <>
              {candidates.map((c) => (
                <button key={c.id} className="item" onClick={() => (setParent(c.id), close())}>
                  <Icon name="liste" size={16} />
                  <span className="truncate">{c.title}</span>
                </button>
              ))}
            </>
          )}
        </Popover>
      )}
    </section>
  );
}

export function ChecklistSection({ task, readOnly }: { task: Task; readOnly?: boolean }) {
  const { client, version } = useSync();
  const plan = usePlan();
  const items = useMemo(() => checklistOf(client, task.id), [client, version, task.id]);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  /** Mehrere Zeilen (eingefügte Liste) werden zu mehreren Punkten; Aufzählungszeichen fallen weg. */
  async function addLines(raw: string) {
    const lines = raw.split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-*•]|\[[ x]\]|\d+[.)])\s*/i, '').trim()).filter(Boolean);
    for (const l of lines) await client.addChecklistItem(task.id, l.slice(0, 300));
    input.current?.focus();
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const raw = text;
    setText('');
    await addLines(raw);
  }

  function drop(target: ChecklistItem) {
    if (!drag || drag === target.id) return;
    const rest = items.filter((i) => i.id !== drag);
    const at = rest.findIndex((i) => i.id === target.id);
    const moving = items.findIndex((i) => i.id === drag) < items.findIndex((i) => i.id === target.id);
    // Nach unten gezogen: hinter das Ziel, nach oben: davor
    const before = moving ? rest[at] : rest[at - 1];
    const after = moving ? rest[at + 1] : rest[at];
    const order = before && after ? (before.order + after.order) / 2 : before ? before.order + 1000 : after!.order - 1000;
    void client.updateChecklistItem(drag, { order });
    setDrag(null);
  }

  async function toSubtask(item: ChecklistItem) {
    if (!plan.more('openTasks')) return;
    await client.saveTask({ projectId: task.projectId, title: item.text.slice(0, 200), parentId: task.parentId ?? task.id });
    await client.deleteChecklistItem(item.id);
  }

  const done = items.filter((i) => i.done).length;
  if (readOnly && !items.length) return null;
  return (
    <section>
      <h4 className="mb-2 flex items-center text-[13px] font-semibold text-[var(--muted)]">
        Checkliste
        <Progress done={done} total={items.length} />
      </h4>
      {items.length > 0 && (
        <ul className="m-0 mb-2 flex list-none flex-col gap-0.5 p-0">
          {items.map((i) => (
            <li
              key={i.id}
              className={`checkitem ${drag === i.id ? 'dragging' : ''}`}
              draggable={!readOnly && editing !== i.id}
              onDragStart={() => setDrag(i.id)}
              onDragEnd={() => setDrag(null)}
              onDragOver={(e) => drag && e.preventDefault()}
              onDrop={() => drop(i)}
            >
              <button
                className={`checkbox ${i.done ? 'on' : ''}`}
                role="checkbox"
                aria-checked={i.done}
                aria-label={i.text}
                disabled={readOnly}
                onClick={() => void client.updateChecklistItem(i.id, { done: !i.done })}
                data-tip={i.done && i.doneByName ? `Abgehakt von ${i.doneByName}${i.doneAt ? `, ${shortDate(i.doneAt)}` : ''}` : undefined}
              >
                {i.done && <Icon name="check" size={13} />}
              </button>
              {editing === i.id ? (
                <input
                  className="input h-8 min-w-0 flex-1 text-[14px]"
                  defaultValue={i.text}
                  autoFocus
                  maxLength={300}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setEditing(null);
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                  onBlur={(e) => {
                    const v = e.currentTarget.value.trim();
                    if (v && v !== i.text) void client.updateChecklistItem(i.id, { text: v });
                    setEditing(null);
                  }}
                />
              ) : (
                <span
                  className={`min-w-0 flex-1 text-[14px] ${i.done ? 'text-[var(--muted)] line-through' : ''}`}
                  onDoubleClick={() => !readOnly && setEditing(i.id)}
                >
                  {i.text}
                </span>
              )}
              {!readOnly && editing !== i.id && (
                <span className="checkacts">
                  <button className="iconbtn plain" style={{ width: 26, height: 26 }} aria-label="Bearbeiten" data-tip="Bearbeiten" onClick={() => setEditing(i.id)}>
                    <Icon name="edit" size={14} />
                  </button>
                  {!i.done && (
                    <button className="iconbtn plain" style={{ width: 26, height: 26 }} aria-label="Als Unteraufgabe anlegen" data-tip="Als Unteraufgabe (eigener Chat)" onClick={() => void toSubtask(i)}>
                      <Icon name="liste" size={14} />
                    </button>
                  )}
                  <button className="iconbtn plain" style={{ width: 26, height: 26 }} aria-label="Entfernen" data-tip="Entfernen" onClick={() => void client.deleteChecklistItem(i.id)}>
                    <Icon name="trash" size={14} />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {!readOnly && (
        <form className="flex gap-2" onSubmit={(e) => void add(e)}>
          <input
            ref={input}
            className="input h-9 min-w-0 flex-1 text-[14px]"
            placeholder="Punkt hinzufügen"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData('text');
              if (pasted.includes('\n')) {
                e.preventDefault();
                const raw = `${text}\n${pasted}`;
                setText('');
                void addLines(raw);
              }
            }}
            aria-label="Neuer Checklistenpunkt"
          />
          {text.trim() && <button className="btn btn-primary h-9">Hinzufügen</button>}
        </form>
      )}
    </section>
  );
}
